import { ComponentChildren, Ref } from 'preact';
import { useEffect, useRef, useState, useCallback, useMemo } from 'preact/hooks';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';
import { ChevronLeft } from '../UNSAFE_RedwoodIcons/ChevronLeft';
import { ChevronRight } from '../UNSAFE_RedwoodIcons/ChevronRight';
import { ChevronUp } from '../UNSAFE_RedwoodIcons/ChevronUp';
import { ChevronDown } from '../UNSAFE_RedwoodIcons/ChevronDown';
import { classNames as clsx } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ConveyorBeltStyles.css';
import { useUser } from '../hooks/UNSAFE_useUser';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { ConveyorBeltContext } from './ConveyorBeltContext';
import { TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { ConveyorBeltVariantOptions } from './themes/ConveyorBeltStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { ConveyorBeltRedwoodTheme } from './themes/redwood/ConveyorBeltTheme';

export type ConveyorBeltProps = TestIdProps & {
  /**
   * ConveyorBelt Component content
   */
  children?: ComponentChildren;
  /**
   * Sets the number of pixels that an element's content is scrolled from its initial position.
   */
  scrollPosition?: number;
  /**
   * Callback that is executed every time conveyor belt is scrolled and the scroll position is changed.
   * @param value The value is the new scroll position of the conveyor belt.
   * @returns
   */
  onScrollPositionChanged?: (value?: number) => void;
  /**
   * Indicates whether overflow content arrows are visible or hidden.
   * "auto" show overflow arrows on desktop, hide on mobile.
   * "visible" always show overflow arrows.
   * "hidden" never show overflow arrows.
   */
  arrowVisibility?: 'auto' | 'visible' | 'hidden';

  /**
   * Specify the orientation of the conveyorBelt.
   * "horizontal" Orient the conveyorBelt horizontally.
   * "vertical" Orient the conveyorBelt vertically.
   */
  orientation?: 'horizontal' | 'vertical';
};

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if runnning on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone';
}

type ConveyorItemElement = {
  item: HTMLElement;
  isVisible: boolean;
  isCurrent: boolean;
  index: number;
};

// utility hook that calculates which conveyorbelt items are visible inside conveyorbelt viewport
// and which are hidden
const useConveyorElementsVisible = (
  root: HTMLDivElement,
  orientation: 'horizontal' | 'vertical',
  prevBtn?: HTMLDivElement,
  nextBtn?: HTMLDivElement,
  direction?: 'ltr' | 'rtl'
) => {
  const observerRef = useRef<IntersectionObserver>();
  const targetsRef = useRef<ConveyorItemElement[]>([]);
  const [targets] = useState<ConveyorItemElement[]>([]);
  const addTarget = useCallback(
    (target: ConveyorItemElement) => {
      if (targetsRef.current !== null) {
        targetsRef.current.push(target);
        targets.push(target);
      }
    },
    [targets]
  );

  useEffect(() => {
    if (targets && targets.length > 0 && root) {
      // IntersectionObserver calls visibility change only when item is
      // fully visible inside (threshold: 1)
      // conveyorbelt viewport (root),
      // taking arrow buttons into the account
      // and viewport should be smaller by the buttons width (rootMargin)
      const rootMargin =
        orientation === 'horizontal'
          ? direction === 'ltr'
            ? `0px ${nextBtn ? -nextBtn.offsetWidth : 0}px 0px ${
                prevBtn ? -prevBtn.offsetWidth : 0
              }px`
            : `0px ${prevBtn ? -prevBtn.offsetWidth : 0}px 0px ${
                nextBtn ? -nextBtn.offsetWidth : 0
              }px`
          : `${nextBtn ? -nextBtn.offsetHeight : 0}px 0px ${
              prevBtn ? -prevBtn.offsetHeight : 0
            }px 0px`;

      observerRef.current = new IntersectionObserver(onVisibilityChange, {
        root,
        rootMargin: rootMargin,
        threshold: 1
      });
      for (const target of targets) {
        if (target != null) observerRef.current.observe(target.item);
      }
    }
    return () => {
      for (const target of targets) {
        if (observerRef.current && target != null) observerRef.current.unobserve(target.item);
      }
    };
  }, [targets, targets.length, root, nextBtn, prevBtn, direction]);

  // handle visibility changes
  const onVisibilityChange = (entries: IntersectionObserverEntry[]) => {
    const newItems: ConveyorItemElement[] = targetsRef.current.map((conveyorItem, index) => {
      const foundEntry = entries.find((entry) => entry.target === conveyorItem.item);
      // if visibility changed for the item, change isVisible property,
      // otherwise just return the original
      if (foundEntry) {
        return {
          item: foundEntry.target as HTMLDivElement,
          isVisible: foundEntry.isIntersecting,
          isCurrent: conveyorItem.isCurrent,
          index: index
        };
      } else {
        return conveyorItem;
      }
    });
    targetsRef.current = newItems;
  };

  return useMemo(
    () => ({
      itemElementsRef: targetsRef,
      addItem: addTarget
    }),
    [targetsRef, addTarget]
  );
};

export type ScrollableHandle = {
  scrollElementIntoView: (element: HTMLElement) => void;
};

/**
 * The Conveyor belt component is a container element that manages
 * overflow for its child elements and allows scrolling among them
 */
export const ConveyorBelt = forwardRef(
  (
    {
      children,
      scrollPosition,
      onScrollPositionChanged,
      arrowVisibility = 'auto',
      orientation = 'horizontal',
      testId
    }: ConveyorBeltProps,
    ref: Ref<ScrollableHandle> = null
  ) => {
    const [canPaginateNext, setCanPaginateNext] = useState(false);
    const [canPaginatePrevious, setCanPaginatePrevious] = useState(false);
    const overflowContainerRef = useRef<HTMLDivElement | null>(null);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);
    const nextButtonContainerRef = useRef<HTMLDivElement | null>(null);
    const prevButtonContainerRef = useRef<HTMLDivElement | null>(null);
    const baseRef = useRef<HTMLDivElement | null>(null);
    const leftRef = useRef<HTMLDivElement | null>(null);
    const rightRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<any>(null);
    const currentItemRef = useRef<HTMLElement | null>(null);
    const [currentItem, _setCurrentItem] = useState<HTMLElement | null>(null);
    const [buttonsHidden, setButtonsHidden] = useState(
      (isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden'
    );
    const { direction } = useUser();
    const { itemElementsRef, addItem } = useConveyorElementsVisible(
      overflowContainerRef.current!,
      orientation,
      prevButtonContainerRef.current!,
      nextButtonContainerRef.current!,
      direction
    );

    useImperativeHandle(ref, () => ({
      scrollElementIntoView: (element: HTMLElement) => {
        scrollIntoView(element);
      },
      scrollPrevious: () => {
        scrollToPreviousPage();
      },
      scrollNext: () => {
        scrollToNextPage();
      }
    }));

    const testIdProps = useTestId(testId);
    const leftArrowTestIdProps = 'leftArrow' + testId;
    const rightArrowTestIdProps = 'rightArrow' + testId;

    const { variantClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        orientation
      }
    );

    const { variantClasses: contentClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        content: orientation
      }
    );

    const { variantClasses: overflowClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        pagination:
          canPaginateNext && canPaginatePrevious
            ? 'both'
            : canPaginateNext
            ? 'next'
            : canPaginatePrevious
            ? 'previous'
            : 'none',
        direction,
        orientation,
        overflow: orientation,
        arrowVisibility: buttonsHidden === true ? 'hidden' : 'visible'
      }
    );

    const { classes: nextButtonClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        nextButton: orientation,
        direction
      }
    );

    const { classes: previousButtonClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        previousButton: orientation,
        direction
      }
    );

    // utility method to get all conveyorbelt items
    const _getConveyorItems = () => {
      const conveyorContentChildren = contentContainerRef.current
        ? Array.from(contentContainerRef.current.querySelectorAll('[data-oj-conveyorbelt-item]'))
        : [];
      if (conveyorContentChildren) {
        let index = -1;
        for (const child of conveyorContentChildren) {
          index++;
          const element = child as HTMLElement;
          if (element) {
            const item = itemElementsRef.current.find((item) => item.item === element);
            if (item == null) {
              addItem({
                item: element!,
                isVisible: false,
                index: index,
                isCurrent: false
              });
            }
          }
        }
      }
    };

    useEffect(() => {
      _getConveyorItems();
    });

    const scrollEndHandler = useCallback(() => {
      if (onScrollPositionChanged)
        onScrollPositionChanged(overflowContainerRef.current?.scrollLeft);
    }, [onScrollPositionChanged]);

    useEffect(() => {
      setButtonsHidden((isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden');
    }, [arrowVisibility]);

    useEffect(() => {
      if (!overflowContainerRef.current) return;
      overflowContainerRef.current.scrollTo({
        left: scrollPosition
      });
    }, [scrollPosition]);

    // useEffect hook that calculates when arrow buttons should become visible or hidden
    // using IntersectionObserver
    useEffect(() => {
      const overflowContainer = overflowContainerRef.current;
      const leftGuard = leftRef.current;
      const rightGuard = rightRef.current;

      if (overflowContainer && leftGuard && rightGuard) {
        if (!buttonsHidden) {
          observerRef.current = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.target.getAttribute('data-intersection-id') === 'left') {
                  setCanPaginatePrevious(!entry.isIntersecting);
                } else {
                  setCanPaginateNext(!entry.isIntersecting);
                }
              });
            },
            {
              root: overflowContainer
            }
          );
          observerRef.current.observe(leftGuard);
          observerRef.current.observe(rightGuard);
        }
        overflowContainer.addEventListener('scrollend', scrollEndHandler);
      }

      // Clean-up
      return () => {
        if (!overflowContainer) return;
        overflowContainer.removeEventListener('scrollend', scrollEndHandler);
        observerRef.current?.unobserve(leftGuard);
        observerRef.current?.unobserve(rightGuard);
      };
    }, [buttonsHidden, scrollEndHandler]);

    const scrollIntoView = useCallback(
      (element: HTMLElement) => {
        // utility method to check if the element is closer to the end edge of the conveyorbelt
        const _shouldSnapToTheEndEdge = (
          ltr: boolean,
          orientation: 'horizontal' | 'vertical',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          let right;
          let left;
          let bottom;

          if (scroller === document.documentElement) {
            left = 0;
            right = document.documentElement.clientWidth;
            bottom = document.documentElement.clientHeight;
          } else {
            const scrollerBounds = scroller.getBoundingClientRect();
            right = canPaginateNext
              ? scrollerBounds.right - nextButtonContainerRef.current?.offsetWidth!
              : scrollerBounds.right;
            left = canPaginateNext
              ? scrollerBounds.left - nextButtonContainerRef.current?.offsetWidth!
              : scrollerBounds.left;
            bottom = canPaginateNext
              ? scrollerBounds.bottom - nextButtonContainerRef.current?.offsetHeight!
              : scrollerBounds.bottom;
          }
          const bounds = elem.getBoundingClientRect();
          return orientation === 'horizontal'
            ? ltr
              ? bounds.right > right
              : bounds.left < left
            : bounds.bottom > bottom;
        };

        // utility method to check that an element is fully visible inside conveyorbelt
        const _isElementInsideScrollerBounds = (
          orientation: 'vertical' | 'horizontal',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          let left;
          let right;
          let top;
          let bottom;
          if (scroller === document.documentElement) {
            left = 0;
            right = document.documentElement.clientWidth;
            top = 0;
            bottom = document.documentElement.clientHeight;
          } else {
            const scrollerBounds = scroller.getBoundingClientRect();
            left = canPaginatePrevious
              ? scrollerBounds.left + prevButtonContainerRef.current?.offsetWidth!
              : scrollerBounds.left;
            right = canPaginateNext
              ? scrollerBounds.right - nextButtonContainerRef.current?.offsetWidth!
              : scrollerBounds.right;
            top = canPaginatePrevious
              ? scrollerBounds.top + prevButtonContainerRef.current?.offsetHeight!
              : scrollerBounds.top;
            bottom = canPaginateNext
              ? scrollerBounds.bottom - nextButtonContainerRef.current?.offsetHeight!
              : scrollerBounds.bottom;
          }
          const bounds = elem.getBoundingClientRect();
          return orientation === 'horizontal'
            ? bounds.left <= right &&
                bounds.right <= right &&
                bounds.left >= left &&
                bounds.right >= left
            : bounds.top <= bottom &&
                bounds.bottom <= bottom &&
                bounds.top >= top &&
                bounds.bottom >= top;
        };

        const _isElementOverflowingScrollerBounds = (
          orientation: 'vertical' | 'horizontal',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          const bounds = elem.getBoundingClientRect();
          const scrollerBounds = scroller.getBoundingClientRect();
          return orientation === 'horizontal'
            ? bounds.width > scrollerBounds.width
            : bounds.height > scrollerBounds.height;
        };

        const isElementVisible = _isElementInsideScrollerBounds(
          orientation,
          element,
          overflowContainerRef.current
        );
        if (isElementVisible) {
          return;
        }
        const isElementOverflowing = _isElementOverflowingScrollerBounds(
          orientation,
          element,
          overflowContainerRef.current
        );

        if (!overflowContainerRef.current || !contentContainerRef.current) return;
        const shouldSnapEnd = _shouldSnapToTheEndEdge(
          direction === 'ltr',
          orientation,
          element,
          overflowContainerRef.current
        );
        if (orientation === 'vertical') {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              top:
                element.offsetTop +
                element.offsetHeight -
                overflowContainerRef.current.offsetHeight +
                (nextButtonContainerRef.current?.offsetHeight
                  ? nextButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              top:
                element.offsetTop -
                (prevButtonContainerRef.current?.offsetHeight
                  ? prevButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }

        if (direction === 'ltr') {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              left:
                element.offsetLeft +
                element.offsetWidth -
                overflowContainerRef.current.offsetWidth +
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              left:
                element.offsetLeft -
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              left:
                element.offsetLeft -
                contentContainerRef.current?.offsetWidth! +
                overflowContainerRef.current.offsetWidth -
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              left:
                element.offsetLeft +
                element.offsetWidth -
                contentContainerRef.current?.offsetWidth! +
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      },
      [canPaginateNext, canPaginatePrevious, direction]
    );

    const getNextInvisible = useCallback(() => {
      const nextInvisible = itemElementsRef.current.find((item) => {
        if (
          item.isVisible == false &&
          item.index >= 1 &&
          itemElementsRef.current[item.index - 1].isVisible == true
        ) {
          return true;
        }
        return false;
      });
      return nextInvisible?.item;
    }, [itemElementsRef]);

    const getPreviousInvisible = useCallback(() => {
      const previousInvisible = itemElementsRef.current.find((item) => {
        if (
          item.isVisible == false &&
          item.index < itemElementsRef.current.length - 1 &&
          itemElementsRef.current[item.index + 1].isVisible == true
        ) {
          return true;
        }
        return false;
      });
      return previousInvisible?.item;
    }, [itemElementsRef]);

    // paginates to the previous partially visible or hidden item in the conveyorbelt,
    // so that it is the last visible of the conveyorbelt view port
    const scrollToPreviousPage = useCallback(() => {
      const overflowContainer = overflowContainerRef.current;
      const scrollAmount =
        orientation === 'horizontal'
          ? overflowContainerRef.current?.clientWidth
          : overflowContainerRef.current?.clientHeight;

      if (overflowContainer && scrollAmount) {
        const previousInvisible: HTMLElement | undefined = getPreviousInvisible();
        if (orientation === 'vertical') {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              top: overflowContainer.scrollTop - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              top:
                previousInvisible.offsetTop +
                previousInvisible.offsetHeight -
                overflowContainer.offsetHeight +
                (prevButtonContainerRef.current?.offsetHeight
                  ? prevButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }
        if (direction === 'ltr') {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                previousInvisible.offsetLeft +
                previousInvisible.offsetWidth -
                overflowContainer.offsetWidth +
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                previousInvisible.offsetLeft -
                contentContainerRef.current?.offsetWidth! +
                overflowContainer.offsetWidth -
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      }
    }, [direction, orientation, getPreviousInvisible]);

    // paginates to the next partially visible or hidden item in the conveyorbelt,
    // so that it is the first at the start of the conveyorbelt view port
    const scrollToNextPage = useCallback(() => {
      const overflowContainer = overflowContainerRef.current;
      const scrollAmount =
        orientation === 'horizontal'
          ? overflowContainerRef.current?.clientWidth
          : overflowContainerRef.current?.clientHeight;

      if (overflowContainer && scrollAmount) {
        const nextInvisible: HTMLElement | undefined = getNextInvisible();
        if (orientation === 'vertical') {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              top: overflowContainer.scrollTop + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              top:
                (nextInvisible?.offsetTop ?? 0) -
                (nextButtonContainerRef.current?.offsetHeight
                  ? nextButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }
        if (direction === 'ltr') {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                (nextInvisible?.offsetLeft ?? 0) -
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                -(contentContainerRef.current?.offsetWidth! - (nextInvisible?.offsetLeft ?? 0)) +
                (nextInvisible?.offsetWidth ?? 0) +
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      }
    }, [direction, orientation, getNextInvisible]);

    const setCurrentItem = useCallback(
      (node: HTMLElement | null) => {
        if (currentItemRef.current !== node) {
          // this logic below is needed to make sure only one item is current
          const item = itemElementsRef.current.find((item) => item.item === node);
          const currentItem = itemElementsRef.current.find((item) => item.isCurrent === true);
          if (item === currentItem || !item) return;
          if (currentItem) currentItem.isCurrent = false;
          item!.isCurrent = true;

          currentItemRef.current = node;
          _setCurrentItem(node);
        }
      },
      [itemElementsRef]
    );

    useEffect(() => {
      if (currentItem && currentItemRef.current) {
        scrollIntoView(currentItemRef.current);
      }
    }, [currentItem]); // eslint warning about scrollIntoView should be ignored.

    const onFocus = useCallback(
      (event: FocusEvent) => {
        if (baseRef.current?.isEqualNode(event.target as HTMLElement)) {
          return;
        }
        scrollIntoView(event.target as HTMLElement);
      },
      [scrollIntoView]
    );

    const previousButtonStyle = clsx([styles.buttonContainer, previousButtonClasses]);

    const nextButtonStyle = clsx([styles.buttonContainer, nextButtonClasses]);

    return (
      <ConveyorBeltContext.Provider value={{ setCurrentItem }}>
        <div
          onFocus={onFocus}
          ref={baseRef}
          className={clsx([styles.conveyorStyle, variantClasses])}
          {...testIdProps}>
          {canPaginatePrevious && !buttonsHidden && (
            <div ref={prevButtonContainerRef} class={previousButtonStyle}>
              <BaseButton
                elementDetails={{ type: 'span', isFocusable: false }}
                styling={['min']}
                size={'sm'}
                variant={'ghost'}
                onAction={() => scrollToPreviousPage()}
                aria-hidden={true}
                testId={leftArrowTestIdProps}>
                <ButtonLabelLayout
                  size={'sm'}
                  display={'icons'}
                  startIcon={
                    orientation === 'horizontal' ? (
                      direction === 'ltr' ? (
                        <ChevronLeft />
                      ) : (
                        <ChevronRight />
                      )
                    ) : (
                      <ChevronUp />
                    )
                  }></ButtonLabelLayout>
              </BaseButton>
            </div>
          )}
          <div
            className={clsx([styles.overflowContainer, overflowClasses])}
            ref={overflowContainerRef}>
            <div
              className={clsx([styles.contentContainer, variantClasses, contentClasses])}
              ref={contentContainerRef}>
              <div
                data-intersection-id="left"
                ref={leftRef}
                style={{ minWidth: 1, minHeight: 1 }}
              />
              {children}
              <div
                data-intersection-id="right"
                ref={rightRef}
                style={{ minWidth: 1, minHeight: 1 }}
              />
            </div>
          </div>
          {canPaginateNext && !buttonsHidden && (
            <div ref={nextButtonContainerRef} class={nextButtonStyle}>
              <BaseButton
                elementDetails={{ type: 'span', isFocusable: false }}
                styling={['min']}
                size={'sm'}
                variant={'ghost'}
                onAction={() => scrollToNextPage()}
                aria-hidden={true}
                testId={rightArrowTestIdProps}>
                <ButtonLabelLayout
                  size={'sm'}
                  display={'icons'}
                  startIcon={
                    orientation === 'horizontal' ? (
                      direction === 'ltr' ? (
                        <ChevronRight />
                      ) : (
                        <ChevronLeft />
                      )
                    ) : (
                      <ChevronDown />
                    )
                  }></ButtonLabelLayout>
              </BaseButton>
            </div>
          )}
        </div>
      </ConveyorBeltContext.Provider>
    );
  }
);
